import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { PageWrapper, Card, Button } from "../components";
import { 
  Mic, Play, Pause, Plus, Library, Sparkles, 
  ChevronRight, Volume2, Loader2, Trash2, Share2, Copy, Heart, Lock
} from "lucide-react";
import { ShareInstallationCard } from "../components/ShareInstallationCard";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const VoiceStudioPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "create";
  
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [installations, setInstallations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [dailyAffirmation, setDailyAffirmation] = useState(null);
  const [playingId, setPlayingId] = useState(null);
  const [audioElement, setAudioElement] = useState(null);
  const [shareInstallation, setShareInstallation] = useState(null);
  const [aiVoiceUsage, setAiVoiceUsage] = useState(null);
  const [playingAiDemo, setPlayingAiDemo] = useState(false);
  const [aiDemoAudio, setAiDemoAudio] = useState(null);

  const getToken = () => localStorage.getItem("reclaim_token");

  useEffect(() => {
    fetchData();
    return () => {
      // Cleanup audio on unmount
      if (audioElement) {
        audioElement.pause();
      }
    };
  }, []);

  const fetchData = async () => {
    const authToken = getToken();
    if (!authToken) {
      setLoading(false);
      return;
    }

    try {
      const [statusRes, installsRes, catsRes, dailyRes, aiUsageRes] = await Promise.all([
        fetch(`${API_URL}/api/voice-studio/status`, {
          headers: { Authorization: `Bearer ${authToken}` }
        }),
        fetch(`${API_URL}/api/voice-studio/my-installations`, {
          headers: { Authorization: `Bearer ${authToken}` }
        }),
        fetch(`${API_URL}/api/voice-studio/categories`),
        fetch(`${API_URL}/api/voice-studio/daily-affirmation`),
        fetch(`${API_URL}/api/voice-studio/ai-voice-usage`, {
          headers: { Authorization: `Bearer ${authToken}` }
        })
      ]);

      const [statusData, installsData, catsData, dailyData, aiUsageData] = await Promise.all([
        statusRes.json(),
        installsRes.json(),
        catsRes.json(),
        dailyRes.json(),
        aiUsageRes.json()
      ]);

      setStatus(statusData);
      setInstallations(installsData.installations || []);
      setCategories(catsData.categories || []);
      setDailyAffirmation(dailyData);
      setAiVoiceUsage(aiUsageData);
    } catch (e) {
      console.error("Error fetching Voice Studio data:", e);
    } finally {
      setLoading(false);
    }
  };

  const handlePlayInstallation = (installation) => {
    if (playingId === installation.id) {
      // Stop playing
      if (audioElement) {
        audioElement.pause();
        audioElement.currentTime = 0;
      }
      setPlayingId(null);
      return;
    }

    // Stop any currently playing
    if (audioElement) {
      audioElement.pause();
    }

    // Play new audio
    const audio = new Audio(installation.audio_url);
    audio.onended = () => setPlayingId(null);
    audio.play();
    setAudioElement(audio);
    setPlayingId(installation.id);
  };

  const handleDeleteInstallation = async (installationId) => {
    // Immediate feedback
    toast.info("Deleting...");
    
    const authToken = getToken();
    try {
      const response = await fetch(`${API_URL}/api/voice-studio/installations/${installationId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || "Failed to delete");
      }
      
      setInstallations(prev => prev.filter(i => i.id !== installationId));
      toast.success("Installation deleted!");
    } catch (e) {
      console.error("Error deleting installation:", e);
      toast.error("Delete failed: " + e.message);
    }
  };

  const handleDuplicateInstallation = (installation) => {
    // Navigate to create page with pre-filled data from the installation
    const params = new URLSearchParams({
      text: installation.affirmation_text,
      style: installation.voice_style || "calm_meditation",
      background: installation.background_audio || "silence",
      binaural: installation.binaural_beat || "none",
      duplicate: "true"
    });
    navigate(`/voice-studio/create?${params.toString()}`);
    toast.success("Duplicating installation - customize and save!");
  };

  const handleCreateDaily = () => {
    if (!dailyAffirmation) return;
    navigate(`/voice-studio/create?text=${encodeURIComponent(dailyAffirmation.text)}`);
  };

  // Play AI voice demo/preview
  const playAiVoiceDemo = async () => {
    // Stop if already playing
    if (playingAiDemo && aiDemoAudio) {
      aiDemoAudio.pause();
      aiDemoAudio.currentTime = 0;
      setPlayingAiDemo(false);
      return;
    }

    try {
      const authToken = getToken();
      // Get demo or preview URL based on access
      const endpoint = aiVoiceUsage?.has_access 
        ? `${API_URL}/api/voice-studio/ai-voice-preview`
        : `${API_URL}/api/voice-studio/ai-voice-demo`;
      
      const headers = aiVoiceUsage?.has_access ? { Authorization: `Bearer ${authToken}` } : {};
      const response = await fetch(endpoint, { headers });
      const data = await response.json();

      const audioUrl = data.preview_url || data.demo_url;
      if (audioUrl) {
        const fullUrl = audioUrl.startsWith('/') ? `${API_URL}${audioUrl}` : audioUrl;
        
        const audio = new Audio(fullUrl);
        audio.onended = () => setPlayingAiDemo(false);
        audio.play();
        setAiDemoAudio(audio);
        setPlayingAiDemo(true);
      }
    } catch (err) {
      console.error("Error playing AI voice demo:", err);
      toast.error("Could not play demo");
    }
  };

  if (loading) {
    return (
      <PageWrapper>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-[#4A6741] border-t-transparent rounded-full animate-spin" />
        </div>
      </PageWrapper>
    );
  }

  const tabs = [
    { id: "create", label: "Create New", icon: Plus },
    { id: "installations", label: "My Installations", icon: Volume2 },
    { id: "moment", label: "Send a Moment", icon: Heart },
    { id: "library", label: "Reclaim Library", icon: Library },
  ];

  return (
    <PageWrapper>
      <div
        className="min-h-screen pb-28"
        style={{
          background:
            "radial-gradient(ellipse at 50% -10%, #11221C 0%, #0A1410 40%, #050705 100%)",
        }}
        data-testid="voice-studio-page"
      >
        {/* Header — EDGE-level premium in emerald/forest palette */}
        <div className="relative overflow-hidden px-6 pt-12 pb-16 text-center">
          {/* Slow drifting emerald glow */}
          <div aria-hidden className="pointer-events-none absolute inset-0 opacity-90"
               style={{ background: "radial-gradient(circle at 50% 35%, rgba(93,180,130,0.14) 0%, transparent 60%)" }} />
          {/* Hairline top gradient */}
          <div aria-hidden className="pointer-events-none absolute top-0 inset-x-0 h-px"
               style={{ background: "linear-gradient(90deg, transparent, rgba(93,180,130,0.55), transparent)" }} />
          <div className="relative max-w-lg mx-auto">
            <motion.div 
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 280, damping: 25 }}
              className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5"
              style={{
                background: "linear-gradient(135deg, rgba(93,180,130,0.32), rgba(93,180,130,0.08))",
                boxShadow: "0 12px 34px -14px rgba(93,180,130,0.55), inset 0 1px 0 rgba(255,255,255,0.08)",
              }}
            >
              <Mic size={28} className="text-emerald-200/95" strokeWidth={1.6} />
            </motion.div>
            <p className="text-[10px] uppercase tracking-[0.5em] text-emerald-300/85 mb-2.5 font-medium">
              Studio
            </p>
            <motion.h1 
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.12 }}
              className="text-white mb-1.5"
              style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: "36px", fontWeight: 500, letterSpacing: "-0.012em" }}
            >
              Voice Studio
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.22 }}
              className="text-white/55 italic"
              style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: "15px" }}
            >
              Record affirmations in your own powerful voice
            </motion.p>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-4 -mt-8 max-w-lg mx-auto">
          <div className="rounded-2xl p-1.5 flex gap-1"
               style={{
                 background: "rgba(15,22,18,0.92)",
                 border: "1px solid rgba(93,180,130,0.16)",
                 boxShadow: "0 14px 36px -18px rgba(93,180,130,0.35), inset 0 1px 0 rgba(255,255,255,0.04)",
               }}>
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 py-3 px-2 rounded-xl text-sm font-medium transition-all flex items-center justify-center gap-1.5 ${
                  activeTab === tab.id
                    ? "text-white"
                    : "text-white/55 hover:text-white/85"
                }`}
                style={activeTab === tab.id ? {
                  background: "linear-gradient(135deg, rgba(93,180,130,0.28), rgba(70,140,100,0.22))",
                  border: "1px solid rgba(93,180,130,0.35)",
                } : {}}
              >
                <tab.icon size={16} />
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="px-6 mt-6 max-w-lg mx-auto space-y-6">
          <AnimatePresence mode="wait">
            {activeTab === "create" && (
              <motion.div
                key="create"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="space-y-6"
              >
                {/* Voice Status */}
                <Card className="p-8 text-center space-y-6 bg-gradient-to-br from-white to-[#F8F7F5] border-2 border-[#4A6741]/20 shadow-lg">
                  <motion.div 
                    whileHover={{ scale: 1.05 }}
                    className="w-24 h-24 bg-gradient-to-br from-[#4A6741] to-[#3D5A34] rounded-full flex items-center justify-center mx-auto shadow-xl shadow-[#4A6741]/30"
                  >
                    <Mic size={40} className="text-white" />
                  </motion.div>
                  <div>
                    <h3 className="font-heading text-2xl text-[#1F2937] mb-2">
                      Record Your Voice
                    </h3>
                    <p className="text-[#6B7280]">
                      Your own voice is the most powerful tool for transformation. 
                      Record affirmations and meditations in your authentic voice.
                    </p>
                  </div>
                  <Button 
                    onClick={(e) => { 
                      e.stopPropagation(); 
                      e.preventDefault(); 
                      navigate("/voice-studio/record-affirmation"); 
                    }} 
                    className="w-full py-4 text-lg"
                    data-testid="record-voice-btn"
                  >
                    <Mic size={22} />
                    Start Recording
                  </Button>
                  <p className="text-xs text-[#9CA3AF]">
                    No external apps needed • Works on all devices
                  </p>
                </Card>

                {/* AI Clone Voice Card - Premium Feature */}
                <Card className={`p-6 text-center space-y-4 border-2 ${
                  aiVoiceUsage?.has_access 
                    ? "bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200/50" 
                    : "bg-gray-50 border-gray-200"
                }`}>
                  <div className="flex justify-between items-start">
                    <motion.div 
                      whileHover={{ scale: 1.05 }}
                      className={`w-16 h-16 rounded-full flex items-center justify-center shadow-lg ${
                        aiVoiceUsage?.has_access 
                          ? "bg-gradient-to-br from-amber-400 to-orange-500 shadow-amber-300/30" 
                          : "bg-gray-300"
                      }`}
                    >
                      <Sparkles size={28} className="text-white" />
                    </motion.div>
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                      aiVoiceUsage?.has_access 
                        ? "bg-amber-100 text-amber-700" 
                        : "bg-gray-200 text-gray-600"
                    }`}>
                      {aiVoiceUsage?.has_access ? "Premium" : "Premium Only"}
                    </span>
                  </div>
                  
                  <div className="text-left">
                    <h3 className="font-heading text-xl text-[#1F2937] mb-1">
                      AI Clone Voice
                    </h3>
                    {aiVoiceUsage?.has_access ? (
                      <>
                        <p className="text-[#6B7280] text-sm mb-3">
                          Use AI to generate affirmations in your cloned voice. 
                          {aiVoiceUsage?.has_voice_clone 
                            ? ` Using: ${aiVoiceUsage?.voice_clone?.name || "My Voice"}`
                            : " Set up your voice clone first."}
                        </p>
                        
                        {/* Usage Stats */}
                        <div className="bg-white/80 rounded-lg p-3 mb-3">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-sm text-gray-600">Monthly Usage</span>
                            <span className="text-sm font-medium text-amber-700">
                              {aiVoiceUsage?.used_this_month || 0} / {aiVoiceUsage?.monthly_limit || 20}
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-gradient-to-r from-amber-400 to-orange-500 h-2 rounded-full transition-all"
                              style={{ 
                                width: `${Math.min(100, ((aiVoiceUsage?.used_this_month || 0) / (aiVoiceUsage?.monthly_limit || 20)) * 100)}%` 
                              }}
                            />
                          </div>
                          {!aiVoiceUsage?.can_generate && (
                            <p className="text-xs text-amber-600 mt-2">
                              Monthly limit reached. Contact us to increase.
                            </p>
                          )}
                        </div>

                        {aiVoiceUsage?.has_voice_clone ? (
                          <>
                            <Button 
                              onClick={() => navigate("/voice-studio/create?use_ai_voice=true")}
                              disabled={!aiVoiceUsage?.can_generate}
                              className={`w-full py-3 ${
                                aiVoiceUsage?.can_generate 
                                  ? "bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600" 
                                  : "bg-gray-300 cursor-not-allowed"
                              }`}
                              data-testid="create-with-ai-btn"
                            >
                              <Sparkles size={18} />
                              Create with AI Voice
                            </Button>
                            {/* Preview button for premium users */}
                            <button
                              onClick={playAiVoiceDemo}
                              className="mt-2 w-full py-2 rounded-lg border border-amber-200 flex items-center justify-center gap-2 text-sm text-amber-600 hover:bg-amber-50 transition-colors"
                              data-testid="preview-voice-btn"
                            >
                              {playingAiDemo ? <Pause size={16} /> : <Play size={16} />}
                              {playingAiDemo ? "Stop Preview" : "Preview my voice"}
                            </button>
                          </>
                        ) : (
                          <Button 
                            onClick={() => navigate("/voice-studio/clone-setup")}
                            className="w-full py-3 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                            data-testid="setup-voice-clone-btn"
                          >
                            <Mic size={18} />
                            Set Up Voice Clone
                          </Button>
                        )}
                      </>
                    ) : (
                      <>
                        <p className="text-[#6B7280] text-sm mb-4">
                          Upgrade to Premium to use AI voice cloning. Generate affirmations in your own cloned voice.
                        </p>
                        <div className="flex items-center justify-center gap-2 p-3 bg-gray-100 rounded-lg mb-3">
                          <Lock size={18} className="text-gray-400" />
                          <span className="text-gray-500 text-sm">Available with Premium</span>
                        </div>
                        {/* Demo button for free users */}
                        <button
                          onClick={playAiVoiceDemo}
                          className="w-full py-2.5 rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-center gap-2 text-sm text-amber-600 hover:bg-amber-100 transition-colors"
                          data-testid="listen-demo-btn"
                        >
                          {playingAiDemo ? <Pause size={16} /> : <Play size={16} />}
                          {playingAiDemo ? "Stop Demo" : "Listen to Demo"}
                        </button>
                      </>
                    )}
                  </div>
                </Card>
              </motion.div>
            )}

            {activeTab === "installations" && (
              <motion.div
                key="installations"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="space-y-4"
              >
                {installations.length === 0 ? (
                  <Card className="p-8 text-center">
                    <div className="w-16 h-16 bg-[#F3F4F6] rounded-full flex items-center justify-center mx-auto mb-4">
                      <Volume2 size={24} className="text-[#9CA3AF]" />
                    </div>
                    <h3 className="font-heading text-lg text-[#1F2937] mb-2">
                      No Installations Yet
                    </h3>
                    <p className="text-[#6B7280] text-sm mb-4">
                      Create your first voice installation to see it here
                    </p>
                    <Button onClick={() => setActiveTab("create")} variant="outline">
                      Create First Installation
                    </Button>
                  </Card>
                ) : (
                  installations.map(installation => (
                    <Card key={installation.id} className="p-4">
                      <div className="flex items-start gap-4">
                        <button
                          onClick={() => handlePlayInstallation(installation)}
                          className={`w-12 h-12 rounded-full flex items-center justify-center transition-all flex-shrink-0 mt-1 ${
                            playingId === installation.id
                              ? "bg-[#4A6741] text-white"
                              : "bg-[#4A6741]/10 text-[#4A6741]"
                          }`}
                          data-testid={`play-installation-${installation.id}`}
                        >
                          {playingId === installation.id ? (
                            <Pause size={20} />
                          ) : (
                            <Play size={20} className="ml-0.5" />
                          )}
                        </button>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-[#1F2937] truncate">
                            {installation.title}
                          </h4>
                          {/* Affirmation text preview */}
                          <p className="text-sm text-[#4A6741] italic mt-1 line-clamp-2">
                            "{installation.affirmation_text}"
                          </p>
                          <div className="flex items-center gap-2 mt-2 flex-wrap">
                            <span className="text-xs px-2 py-0.5 bg-[#F3F4F6] rounded-full text-[#6B7280]">
                              {installation.voice_style_name}
                            </span>
                            {installation.background_audio_name !== "Silence" && (
                              <span className="text-xs px-2 py-0.5 bg-[#F3F4F6] rounded-full text-[#6B7280]">
                                {installation.background_audio_name}
                              </span>
                            )}
                            {installation.binaural_beat && installation.binaural_beat !== "none" && (
                              <span className="text-xs px-2 py-0.5 bg-[#F3F4F6] rounded-full text-[#6B7280]">
                                {installation.binaural_beat_name}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-[#9CA3AF] mt-2">
                            {new Date(installation.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex flex-col gap-2">
                          <button
                            onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleDuplicateInstallation(installation); }}
                            className="p-3 rounded-full text-[#6B7280] hover:text-[#4A6741] hover:bg-[#4A6741]/10 transition-colors active:bg-[#4A6741]/20"
                            title="Duplicate"
                            data-testid={`duplicate-installation-${installation.id}`}
                          >
                            <Copy size={20} />
                          </button>
                          <button
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              e.preventDefault(); 
                              setShareInstallation(installation); 
                            }}
                            className="p-3 rounded-full text-[#6B7280] hover:text-[#4A6741] hover:bg-[#4A6741]/10 transition-colors active:bg-[#4A6741]/20"
                            title="Share"
                            data-testid={`share-installation-${installation.id}`}
                          >
                            <Share2 size={20} />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleDeleteInstallation(installation.id); }}
                            className="p-3 rounded-full text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors active:bg-red-100"
                            title="Delete"
                            data-testid={`delete-installation-${installation.id}`}
                          >
                            <Trash2 size={20} />
                          </button>
                        </div>
                      </div>
                    </Card>
                  ))
                )}
              </motion.div>
            )}

            {activeTab === "library" && (
              <motion.div
                key="library"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="space-y-4"
              >
                <p className="text-sm text-[#6B7280] text-center">
                  Browse affirmations by category
                </p>
                {categories.map(category => (
                  <Card
                    key={category.id}
                    className="p-4 cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => navigate(`/voice-studio/library/${category.id}`)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-[#4A6741]/10 rounded-full flex items-center justify-center">
                          <Sparkles size={20} className="text-[#4A6741]" />
                        </div>
                        <div>
                          <h4 className="font-medium text-[#1F2937]">{category.name}</h4>
                          <p className="text-xs text-[#6B7280]">{category.description}</p>
                        </div>
                      </div>
                      <ChevronRight size={20} className="text-[#9CA3AF]" />
                    </div>
                  </Card>
                ))}
              </motion.div>
            )}

            {activeTab === "moment" && (
              <motion.div
                key="moment"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
              >
                {/* Dark backdrop wrapper — gives Send a Moment its own world,
                    independent of the green/white studio shell. */}
                <div
                  className="rounded-[32px] px-5 py-6 space-y-6 -mx-2"
                  style={{
                    background:
                      "linear-gradient(180deg, #14110d 0%, #0d0a07 60%, #08060a 100%)",
                    border: "1px solid rgba(203,161,83,0.10)",
                    boxShadow: "0 24px 60px rgba(0,0,0,0.45)",
                  }}
                >
                {/* Hero — premium dark glass on warm gold gradient */}
                <motion.div
                  className="relative overflow-hidden rounded-[28px] px-7 py-9 text-center"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.8 }}
                  style={{
                    background:
                      "radial-gradient(ellipse at 50% 0%, rgba(245,235,196,0.12) 0%, rgba(20,16,14,0.85) 55%, rgba(15,12,10,0.95) 100%)",
                    border: "1px solid rgba(203,161,83,0.22)",
                    boxShadow: "0 12px 48px rgba(0,0,0,0.4), inset 0 0 0 1px rgba(203,161,83,0.05)",
                  }}
                >
                  {/* Floating petal-like glints */}
                  <motion.div
                    className="absolute -top-12 -right-12 w-40 h-40 rounded-full pointer-events-none"
                    animate={{ scale: [1, 1.15, 1], opacity: [0.35, 0.55, 0.35] }}
                    transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
                    style={{ background: "radial-gradient(circle, rgba(203,161,83,0.45) 0%, transparent 70%)" }}
                  />
                  <motion.div
                    className="absolute -bottom-10 -left-10 w-44 h-44 rounded-full pointer-events-none"
                    animate={{ scale: [1, 1.1, 1], opacity: [0.25, 0.45, 0.25] }}
                    transition={{ duration: 11, repeat: Infinity, ease: "easeInOut", delay: 1.5 }}
                    style={{ background: "radial-gradient(circle, rgba(245,235,196,0.30) 0%, transparent 70%)" }}
                  />

                  <p
                    className="text-[10px] uppercase tracking-[0.4em] mb-4"
                    style={{ color: "rgba(203,161,83,0.85)", fontFamily: "'Manrope', sans-serif" }}
                  >
                    <Sparkles size={9} className="inline -mt-0.5 mr-1" /> A small offering
                  </p>
                  <h2
                    className="text-3xl sm:text-4xl mb-3"
                    style={{
                      fontFamily: "'Playfair Display', serif",
                      fontWeight: 400,
                      color: "rgba(245,240,230,0.95)",
                      fontStyle: "italic",
                      letterSpacing: "0.01em",
                    }}
                  >
                    Send a Moment
                  </h2>
                  <p
                    className="text-sm mx-auto max-w-xs leading-relaxed"
                    style={{
                      color: "rgba(245,240,230,0.55)",
                      fontFamily: "'Spectral', serif",
                      fontStyle: "italic",
                    }}
                  >
                    A breath of presence — your voice, held in stillness, sent to someone who needs it.
                  </p>
                </motion.div>

                {/* Three steps — minimal numbered glass list */}
                <div className="space-y-3">
                  {[
                    { num: "01", icon: Mic,      title: "Speak it",  body: "From the heart, in your own voice." },
                    { num: "02", icon: Sparkles, title: "Frame it",  body: "Choose a nature image to hold the words." },
                    { num: "03", icon: Share2,   title: "Send it",   body: "A QR-coded card they can scan and listen, anywhere." },
                  ].map((step, idx) => (
                    <motion.div
                      key={step.num}
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2 + idx * 0.1, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                      className="flex items-center gap-4 rounded-2xl px-5 py-4"
                      style={{
                        background: "rgba(255,255,255,0.025)",
                        border: "1px solid rgba(255,255,255,0.06)",
                        backdropFilter: "blur(12px)",
                      }}
                    >
                      <div
                        className="text-[10px] tracking-[0.2em]"
                        style={{ color: "rgba(203,161,83,0.55)", fontFamily: "'Manrope', sans-serif" }}
                      >
                        {step.num}
                      </div>
                      <div
                        className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ background: "rgba(203,161,83,0.10)", border: "1px solid rgba(203,161,83,0.20)" }}
                      >
                        <step.icon size={15} style={{ color: "rgba(245,235,196,0.85)" }} />
                      </div>
                      <div className="flex-1">
                        <p
                          className="text-[15px]"
                          style={{ fontFamily: "'Playfair Display', serif", color: "rgba(245,240,230,0.92)" }}
                        >
                          {step.title}
                        </p>
                        <p
                          className="text-[12px] mt-0.5"
                          style={{ color: "rgba(245,240,230,0.45)", fontFamily: "'Spectral', serif", fontStyle: "italic" }}
                        >
                          {step.body}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Inspiration whispers */}
                <div>
                  <p
                    className="text-[10px] uppercase tracking-[0.3em] text-center mb-4"
                    style={{ color: "rgba(245,240,230,0.35)", fontFamily: "'Manrope', sans-serif" }}
                  >
                    a few starting notes
                  </p>
                  <div className="grid grid-cols-1 gap-2">
                    {[
                      "Thinking of you today",
                      "Take a breath with me",
                      "You've got this",
                      "A calm moment for you",
                    ].map((msg, idx) => (
                      <motion.div
                        key={idx}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.6 + idx * 0.08 }}
                        className="rounded-xl px-4 py-3 text-center"
                        style={{
                          background: "rgba(245,235,196,0.03)",
                          border: "1px solid rgba(245,235,196,0.08)",
                        }}
                      >
                        <p
                          className="text-[14px] italic"
                          style={{
                            color: "rgba(245,240,230,0.62)",
                            fontFamily: "'Spectral', serif",
                            letterSpacing: "0.01em",
                          }}
                        >
                          “{msg}”
                        </p>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* CTA — gold-on-dark, hand-stitched feel */}
                <motion.button
                  onClick={() => navigate("/voice-studio/gift/create")}
                  whileTap={{ scale: 0.98 }}
                  whileHover={{ y: -1 }}
                  className="w-full rounded-full py-4 flex items-center justify-center gap-3 transition-all"
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(203,161,83,0.92) 0%, rgba(181,137,76,0.95) 100%)",
                    border: "1px solid rgba(245,235,196,0.3)",
                    color: "#1a1410",
                    fontFamily: "'Playfair Display', serif",
                    fontSize: 16,
                    letterSpacing: "0.02em",
                    boxShadow:
                      "0 12px 32px rgba(203,161,83,0.35), inset 0 1px 0 rgba(255,255,255,0.25)",
                  }}
                  data-testid="create-moment-btn"
                >
                  <Heart size={16} fill="#1a1410" /> Begin a Moment
                </motion.button>

                <p
                  className="text-[11px] text-center italic"
                  style={{ color: "rgba(245,240,230,0.30)", fontFamily: "'Spectral', serif" }}
                >
                  Takes about a minute. They'll feel it for longer.
                </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Share Installation Modal */}
      <ShareInstallationCard
        installation={shareInstallation}
        isVisible={!!shareInstallation}
        onClose={() => setShareInstallation(null)}
      />
    </PageWrapper>
  );
};

export default VoiceStudioPage;
